import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUrlState } from "@/lib/navState";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plate, StatusBadge } from "@/components/StatusBadge";
import { phaseLabel, label, formatRupiah } from "@/lib/constants";
import { CARDS, C, NO_FINANCE_ROLES, preValue, finValue, isOverdue, agingDays } from "@/lib/phaseCards";
import { PeriodFilter, computeRange, inRange } from "@/components/PeriodFilter";
import { Search, ArrowLeft, ChevronRight, ClipboardList, Wrench, ReceiptText, Inbox } from "lucide-react";

const SUB = {
  PRE_ORDER: "Unit yang masih dalam proses sebelum masuk bengkel.",
  REPAIR_ORDER: "Unit yang sudah masuk dan sedang dalam proses perbaikan.",
  FINISH_ORDER: "Proses penagihan dan penyelesaian administrasi unit.",
};
const HEAD = {
  PRE_ORDER: { icon: ClipboardList, glow: "rgba(22,132,255,0.4)", ic: "text-blue-300", bg: "bg-blue-500/12", ring: "ring-blue-400/30" },
  REPAIR_ORDER: { icon: Wrench, glow: "rgba(245,158,11,0.4)", ic: "text-amber-300", bg: "bg-amber-500/12", ring: "ring-amber-400/30" },
  FINISH_ORDER: { icon: ReceiptText, glow: "rgba(16,185,129,0.4)", ic: "text-emerald-300", bg: "bg-emerald-500/12", ring: "ring-emerald-400/30" },
};

export default function PhaseQueue({ phase }) {
  const [claims, setClaims] = useState([]);
  const [search, setSearch] = useUrlState("q", "");
  const [selected, setSelected] = useState(null);
  const [period, setPeriod] = useState({ preset: "all", from: "", to: "" });
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewFinance = !NO_FINANCE_ROLES.includes(user?.role_name || "");

  const load = useCallback(async (q = "") => {
    const { data } = await api.get("/claims", { params: { phase, search: q } });
    setClaims(data);
  }, [phase]);
  useEffect(() => { load(); }, [load]);
  const [orMap, setOrMap] = useState({});
  useEffect(() => { api.get("/or-payments/status-map").then((r) => setOrMap(r.data || {})).catch(() => setOrMap({})); }, [phase]);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search, load]);
  useEffect(() => { setSelected(new URLSearchParams(window.location.search).get("bucket") || null); setSearch(""); setPeriod({ preset: "all", from: "", to: "" }); }, [phase]); // eslint-disable-line

  const defs = CARDS[phase] || [];
  const range = useMemo(() => computeRange(period.preset, period.from, period.to), [period]);
  const basis = phase === "REPAIR_ORDER" ? "unit_in_date" : "updated_at";
  const dated = useMemo(() => claims.filter((c) => inRange(c[basis] || c.updated_at, range.from, range.to)), [claims, basis, range]);

  const stats = useMemo(() => defs.map(([k, l, icon, tone, pred, valFn]) => {
    const rows = dated.filter(pred);
    const total = rows.reduce((a, c) => a + Number(valFn(c) || 0), 0);
    const overdue = phase === "FINISH_ORDER" ? rows.filter(isOverdue).length : rows.filter((c) => agingDays(phase, c) > 7).length;
    return { k, l, icon, tone, count: rows.length, total, overdue };
  }), [dated, defs, phase]);

  const rowsFor = (key) => {
    if (key === "ALL") return dated;
    const def = defs.find((d) => d[0] === key);
    return def ? dated.filter(def[4]) : [];
  };
  const visible = selected ? rowsFor(selected) : [];
  const selCard = stats.find((s) => s.k === selected);
  const head = HEAD[phase];

  const OR_BADGE = {
    PENDING_APPROVAL: ["OR PENDING", "border-amber-400/30 bg-amber-500/10 text-amber-300"],
    APPROVED: ["OR DISETUJUI", "border-blue-400/30 bg-blue-500/10 text-blue-300"],
    WAITING_APPROVAL: ["OR PENDING", "border-amber-400/30 bg-amber-500/10 text-amber-300"],
    NEEDS_FIX: ["OR PERBAIKAN", "border-rose-400/30 bg-rose-500/10 text-rose-300"],
    REJECTED: ["OR PERBAIKAN", "border-rose-400/30 bg-rose-500/10 text-rose-300"],
    PAID: ["OR PAID", "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"],
    APPROVED: ["OR PAID", "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"],
  };
  const OR_BELUM = ["OR BELUM BAYAR", "border-[#EBC08F] bg-[#FDF1E4] !text-[#B45309]"];
  const OrBadge = ({ c }) => {
    const ins = c.customer_type ? c.customer_type !== "GENERAL" : !!c.insurance_company && String(c.insurance_company).toUpperCase() !== "UMUM";
    if (!ins || c.awaiting_spk_fix) return null;
    const s = orMap[c.id];
    const [txt, cls] = OR_BADGE[s] || OR_BELUM;
    return <span data-testid={`or-badge-${c.id}`} className={`rounded-full border px-2 py-0.5 text-[12px] font-bold ${cls}`}>{txt}</span>;
  };

  const wrapCls = "ti-dark -m-3 -mb-24 min-h-[calc(100vh-3rem)] px-4 py-4 sm:-m-5 sm:px-6 lg:-mb-5 lg:px-8 lg:py-6";
  const searchInput = "h-11 w-full rounded-xl border border-[#29466D] bg-[#0a2038]/70 pl-10 pr-4 text-sm text-white placeholder:text-[#7E91AC] outline-none transition-colors focus:border-[#1684FF] focus:ring-2 focus:ring-[#1684FF]/20";

  // ============ DASHBOARD (status cards) ============
  if (!selected) {
    return (
      <div className={wrapCls} data-testid={`phase-dashboard-${phase}`}>
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center gap-3">
            <span className={`flex h-11 w-11 items-center justify-center rounded-xl ring-1 ring-inset ${head.bg} ${head.ic} ${head.ring}`} style={{ boxShadow: `0 0 16px ${head.glow}` }}>
              <head.icon size={22} />
            </span>
            <div>
              <h1 className="font-heading text-xl font-extrabold tracking-tight text-white sm:text-2xl">{phaseLabel(phase)}</h1>
              <p className="mt-0.5 text-xs text-[#AAB8CC]">{SUB[phase]}</p>
            </div>
          </div>

          <div className="relative mt-3.5">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7E91AC]" />
            <input data-testid="dash-search-input" placeholder="Cari Nopol / No. Claim / No. SPK / Customer…" value={search} onChange={(e) => setSearch(e.target.value)} onFocus={() => search && setSelected("ALL")} className={searchInput} />
          </div>

          <div className="mt-3.5 grid grid-cols-2 gap-2.5 md:grid-cols-3">
            {stats.map((s, i) => {
              const c = C[s.tone] || C.blue;
              const oddFull = stats.length % 2 === 1 && i === stats.length - 1 ? "col-span-2 md:col-span-1" : "";
              return (
                <button
                  key={s.k}
                  data-testid={`dash-card-${s.k}`}
                  onClick={() => setSelected(s.k)}
                  style={{ animationDelay: `${i * 45}ms` }}
                  className={`ti-fade ti-dcard ti-dcard-hover group relative flex min-h-[112px] flex-col justify-between overflow-hidden p-3 ${oddFull}`}
                >
                  <span className={`absolute inset-y-0 left-0 w-1 ${c.bar}`} />
                  <div className="flex items-start justify-between">
                    <span className={`flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-inset ${c.bg} ${c.ic} ${c.ring}`} style={{ boxShadow: `0 0 12px ${c.glow}` }}><s.icon size={16} /></span>
                    <ChevronRight size={15} className="text-[#4a6285] transition-colors group-hover:text-blue-300" />
                  </div>
                  <div>
                    <div className="font-heading text-3xl font-extrabold leading-none tracking-tight text-white">{s.count}</div>
                    <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#AAB8CC]">{s.l}</div>
                    {canViewFinance && s.total > 0 && <div className="mt-0.5 text-[11px] font-semibold text-slate-300">{formatRupiah(s.total)}</div>}
                    {s.overdue > 0 && <div className="mt-1 inline-flex w-fit items-center gap-1 rounded-full border border-rose-400/30 bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-bold text-rose-300">{s.overdue} terlambat</div>}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="mt-3.5 text-center text-xs text-[#7E91AC]">Klik kartu status untuk melihat daftar unit</p>
        </div>
      </div>
    );
  }

  // ============ DRILLDOWN (detail list) ============
  return (
    <div className={wrapCls} data-testid={`phase-drilldown-${phase}`}>
      <div className="mx-auto max-w-6xl">
        <button data-testid="dash-back-btn" onClick={() => setSelected(null)} className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-[#29466D] bg-[#102544] px-3 py-1.5 text-sm font-medium text-[#C3D0E2] transition-colors hover:border-[#1684FF] hover:text-white"><ArrowLeft size={15} /> Kembali</button>
        <div>
          <h1 className="font-heading text-lg font-bold text-white sm:text-xl">{phaseLabel(phase)} · {selected === "ALL" ? "Semua" : (selCard?.l || selected)}</h1>
          <p className="mt-0.5 text-xs text-[#AAB8CC]">{visible.length} unit{canViewFinance && selCard?.total > 0 ? ` · ${formatRupiah(selCard.total)}` : ""}</p>
        </div>

        <div className="relative mt-3">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7E91AC]" />
          <input data-testid="drill-search-input" placeholder="Cari Nopol / No. Claim / No. SPK / Customer…" value={search} onChange={(e) => setSearch(e.target.value)} className={searchInput.replace("h-11", "h-10")} />
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />

        {/* Desktop table */}
        <div className="ti-dcard mt-3 hidden overflow-hidden md:block">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-[#0a2038] text-[10px] uppercase tracking-wider text-[#AAB8CC]">
                <tr>{["Nopol", "Unit / Customer", "Asuransi", ...(canViewFinance ? ["Nilai"] : []), "Status", "Aging"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-[#1b3357]">
                {[...visible].sort((a, b) => agingDays(phase, b) - agingDays(phase, a)).map((c) => (
                  <tr key={c.id} data-testid={`drill-row-${c.id}`} onClick={() => navigate(`/claims/${c.id}`)} className="cursor-pointer transition-colors hover:bg-white/5">
                    <td className="px-3 py-2"><Plate value={c.license_plate} />{c.wo_number && <div className="mt-1"><span className="inline-flex items-center rounded bg-blue-500/15 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-300 ring-1 ring-inset ring-blue-400/25">{c.wo_number}</span></div>}</td>
                    <td className="px-3 py-2"><div className="text-[13px] font-medium text-white">{[c.vehicle_brand, c.vehicle_model].filter(Boolean).join(" ") || "-"}</div><div className="text-[11px] text-[#AAB8CC]">{c.customer_name || "-"}{c.spk_number ? ` · SPK ${c.spk_number}` : ""}</div></td>
                    <td className="px-3 py-2 text-[12px] text-[#C3D0E2]">{c.insurance_company || "Umum"}</td>
                    {canViewFinance && <td className="px-3 py-2 text-[12px] font-medium text-slate-200">{formatRupiah(phase === "FINISH_ORDER" ? finValue(c) : preValue(c))}</td>}
                    <td className="px-3 py-2"><div className="flex flex-wrap items-center gap-1.5"><StatusBadge status={c.status} />{phase === "REPAIR_ORDER" && c.workshop_stage && <span className="rounded-full border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold text-blue-300">{label(c.workshop_stage)}</span>}{phase === "FINISH_ORDER" && isOverdue(c) && <span className="rounded-full border border-rose-400/25 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold text-rose-300">OVERDUE</span>}<OrBadge c={c} /></div></td>
                    <td className="px-3 py-2 whitespace-nowrap text-[11px] text-[#AAB8CC]">{agingDays(phase, c)}d</td>
                  </tr>
                ))}
                {visible.length === 0 && <tr><td colSpan={canViewFinance ? 6 : 5}><Empty /></td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 md:hidden">
          {[...visible].sort((a, b) => agingDays(phase, b) - agingDays(phase, a)).map((c) => (
            <button key={c.id} data-testid={`drill-card-${c.id}`} onClick={() => navigate(`/claims/${c.id}`)} className="ti-dcard ti-dcard-hover block w-full p-2.5 text-left">
              <div className="flex items-center justify-between gap-2"><Plate value={c.license_plate} /><StatusBadge status={c.status} /></div>
              <div className="mt-1 flex flex-wrap gap-1"><OrBadge c={c} /></div>
              <div className="mt-1.5 truncate text-[13px] font-semibold text-white">{[c.vehicle_brand, c.vehicle_model].filter(Boolean).join(" ") || "-"}</div>
              <div className="truncate text-xs text-[#AAB8CC]">{c.customer_name || "-"}{c.insurance_company ? ` · ${c.insurance_company}` : " · Umum"}</div>
              <div className="mt-1 flex items-center justify-between gap-2 text-xs text-[#AAB8CC]">
                {canViewFinance ? <span className="font-medium text-slate-200">{formatRupiah(phase === "FINISH_ORDER" ? finValue(c) : preValue(c))}</span> : <span />}
                <span className="shrink-0">{agingDays(phase, c)}d{phase === "FINISH_ORDER" && isOverdue(c) ? " · OVERDUE" : ""}</span>
              </div>
            </button>
          ))}
          {visible.length === 0 && <div className="sm:col-span-2"><Empty /></div>}
        </div>
      </div>
    </div>
  );
}

function Empty() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
      <Inbox size={22} className="text-[#4a6285]" />
      <p className="text-sm text-[#AAB8CC]">Tidak ada unit pada status ini.</p>
    </div>
  );
}
